/**
 * Admin Portal → Codes → Issue codes → Summer50 & one-off — `/admin/codes/issue` (Codes PR-8 moved it
 * here from Users → Codes, `/admin/growth`, which redirects; `embedded` drops its own page title).
 *
 * Summer50 and one-off codes — every kind of code Coach Cedar issues from campaign_codes.
 *  • Summer50 campaign pool: generate/allocate waves, funnel + R-metric, inventory.
 *  • One-off (strategic) codes: name + length, minted one at a time for evaluators
 *    and influencers. Do not consume a Summer50 spot; see migration 1699.
 * Plan: docs/plans/SUMMER50_DEV_PLAN.md §S2.2.
 *
 * Roles (Codes PR-1, 2026-09-28): the page is open to every admin role, but issuing,
 * allocating, extending, revoking and the CSV need an admin or super admin — the server
 * answers 403 to support and viewer. Those tools are hidden for them here the way the
 * partner pages hide theirs (useAdminRole: the role comes from the server, and while it is
 * unknown or failed to load, the tools stay hidden — fail closed).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Summer50GrowthService as Svc } from '../../../services/summer50GrowthService';
import { ReadOnlyNote, SAVE_FAILED, saveBlob, useAdminRole } from '../partners/PartnersShared';

type Dash = {
  spots: { redeemed: number; total: number; left: number };
  funnel: { redeemed: number; activated: number; dormant: number; converted_to_paid: number };
  r_metric_7d: number;
  months_earned: number;
  program_llm_cost_usd: number;
  by_source?: { source: string; allocated: number; redeemed: number }[];
};

type CodeRow = {
  id: string; code: string; wave: string; status: string;
  allocated_to_handle: string | null; expires_at: string | null; redemption_count: number;
  code_type: string; duration: string; duration_label: string; note: string | null;
};

type DurationOpt = { key: string; label: string };

type InvRow = { reward_sku: string; available: number; assigned: number; low_stock: boolean; soonest_expiry: string | null };

const card: React.CSSProperties = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, minWidth: 140 };
const btn: React.CSSProperties = { background: '#1F6C4F', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontWeight: 600 };
const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', fontSize: 12, color: '#6b7280' };
const td: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid #f3f4f6', fontSize: 13 };

export function AdminGrowth({ embedded = false, openOneOff = false }: { embedded?: boolean; openOneOff?: boolean } = {}) {
  const { role, writable, loaded } = useAdminRole();
  const [dash, setDash] = useState<Dash | null>(null);
  const [codes, setCodes] = useState<CodeRow[]>([]);
  const [inv, setInv] = useState<InvRow[]>([]);
  const [wave, setWave] = useState('wave-1');
  const [count, setCount] = useState(25);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // One-off "issue a code for a named person" form.
  // The Overview's "+ One-off code" opens the form straight away (`?open=one-off`).
  const [showNamed, setShowNamed] = useState(openOneOff);
  const [namedFirst, setNamedFirst] = useState('');
  const [namedDuration, setNamedDuration] = useState('monthly');
  const [namedNote, setNamedNote] = useState('');
  const [durations, setDurations] = useState<DurationOpt[]>([]);
  const [justCreated, setJustCreated] = useState<{ code: string; label: string } | null>(null);

  const refresh = useCallback(async () => {
    setError('');
    try {
      const [d, c, i] = await Promise.all([
        Svc.dashboard(),
        Svc.listCodes({ status: statusFilter || undefined, code_type: typeFilter || undefined }),
        Svc.inventory(),
      ]);
      setDash(d);
      setCodes(c.codes || []);
      setInv(i.inventory || []);
    } catch (e: any) { setError(e.message || 'Failed to load'); }
  }, [statusFilter, typeFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  // Duration options come from the backend so the dropdown can't drift from the
  // RevenueCat enum. Fetched once; failure leaves the form disabled rather than
  // offering a value the grant would reject.
  useEffect(() => {
    Svc.durations()
      .then((d) => {
        setDurations(d.durations || []);
        if (d.default) setNamedDuration(d.default);
      })
      .catch(() => setDurations([]));
  }, []);

  const doCreateNamed = async () => {
    const name = namedFirst.trim();
    if (!name) { setError('First name is required'); return; }
    setBusy(true); setError('');
    try {
      const r = await Svc.createNamed(name, namedDuration, namedNote.trim());
      setJustCreated({ code: r.code, label: r.duration_label });
      setNamedFirst(''); setNamedNote('');
      setShowNamed(false);
      await refresh();
    } catch (e: any) { setError(e.message); }
    finally { setBusy(false); }
  };

  const doGenerate = async () => {
    setBusy(true); setError('');
    try { await Svc.generateCodes(wave, count); await refresh(); }
    catch (e: any) { setError(e.message); }
    finally { setBusy(false); }
  };

  const doExport = async () => {
    setBusy(true); setError('');
    try {
      // saveBlob answers false when the browser could not be handed the file: say so, never
      // a silent click (review N5).
      if (!saveBlob(await Svc.exportCodes(wave || undefined), 'cedar_codes.csv')) setError(SAVE_FAILED);
    }
    catch (e: any) { setError(e.message); }
    finally { setBusy(false); }
  };

  const act = async (fn: () => Promise<any>) => {
    setBusy(true); setError('');
    try { await fn(); await refresh(); }
    catch (e: any) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ padding: 24, fontFamily: 'Inter, system-ui, sans-serif' }}>
      {embedded
        ? <h2 style={{ marginBottom: 4, marginTop: 0 }}>Summer50 and one-off codes</h2>
        : <h1 style={{ marginBottom: 4 }}>Codes</h1>}
      <p style={{ color: '#6b7280', marginTop: 0 }}>Summer50 campaign, referral program, and one-off codes for evaluators &amp; influencers.</p>
      {error && <div style={{ background: '#FEF2F2', color: '#B91C1C', padding: 12, borderRadius: 8, marginBottom: 16 }}>{error}</div>}
      <ReadOnlyNote writable={writable} loaded={loaded} role={role} />

      {/* Dashboard strip */}
      {dash && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
          <div style={card}><div style={{ fontSize: 12, color: '#6b7280' }}>Spots claimed</div><div style={{ fontSize: 28, fontWeight: 700 }}>{dash.spots.redeemed}/{dash.spots.total}</div><div style={{ fontSize: 12 }}>{dash.spots.left} left</div></div>
          <div style={card}><div style={{ fontSize: 12, color: '#6b7280' }}>Activated</div><div style={{ fontSize: 28, fontWeight: 700 }}>{dash.funnel.activated}</div><div style={{ fontSize: 12 }}>goal created</div></div>
          <div style={card}><div style={{ fontSize: 12, color: '#6b7280' }}>Dormant</div><div style={{ fontSize: 28, fontWeight: 700, color: dash.funnel.dormant > dash.spots.redeemed / 2 ? '#B91C1C' : undefined }}>{dash.funnel.dormant}</div><div style={{ fontSize: 12 }}>inactive 7d</div></div>
          <div style={card}><div style={{ fontSize: 12, color: '#6b7280' }}>Converted</div><div style={{ fontSize: 28, fontWeight: 700 }}>{dash.funnel.converted_to_paid}</div><div style={{ fontSize: 12 }}>to paid</div></div>
          <div style={card}><div style={{ fontSize: 12, color: '#6b7280' }}>R-metric (7d)</div><div style={{ fontSize: 28, fontWeight: 700, color: dash.r_metric_7d >= 1 ? '#B91C1C' : '#1F6C4F' }}>{dash.r_metric_7d}</div><div style={{ fontSize: 12 }}>referred ÷ total {dash.r_metric_7d >= 1 ? '⚠ throttle' : ''}</div></div>
          <div style={card}><div style={{ fontSize: 12, color: '#6b7280' }}>Months earned</div><div style={{ fontSize: 28, fontWeight: 700 }}>{dash.months_earned}</div></div>
          <div style={card}><div style={{ fontSize: 12, color: '#6b7280' }}>Program LLM</div><div style={{ fontSize: 28, fontWeight: 700 }}>${dash.program_llm_cost_usd.toFixed(0)}</div></div>
        </div>
      )}

      {/* Channel split (mig 1653): IG vs FB vs other — allocations + redemptions per source */}
      {dash && (dash.by_source?.length ?? 0) > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h2>Channel split</h2>
          <table style={{ borderCollapse: 'collapse', minWidth: 360 }}>
            <thead><tr><th style={th}>Channel</th><th style={th}>Allocated</th><th style={th}>Redeemed</th></tr></thead>
            <tbody>
              {dash.by_source!.map((s) => (
                <tr key={s.source}>
                  <td style={td}>{s.source.charAt(0).toUpperCase() + s.source.slice(1)}</td>
                  <td style={td}>{s.allocated}</td>
                  <td style={td}>{s.redeemed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Inventory */}
      <h2>Offer-code inventory</h2>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        {inv.length === 0 && <div style={{ color: '#6b7280' }}>No inventory imported yet. Import App Store Connect's CSV on Codes → Store inventory.</div>}
        {inv.map((r) => (
          <div key={r.reward_sku} style={{ ...card, borderColor: r.low_stock ? '#F59E0B' : '#e5e7eb' }}>
            <div style={{ fontSize: 12, color: '#6b7280' }}>{r.reward_sku}</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{r.available} avail {r.low_stock && <span style={{ color: '#F59E0B' }}>⚠ low</span>}</div>
            <div style={{ fontSize: 12 }}>{r.assigned} assigned · exp {r.soonest_expiry ? new Date(r.soonest_expiry).toLocaleDateString() : '—'}</div>
          </div>
        ))}
      </div>

      {/* Code generation — admin roles only (the server refuses support and viewer). */}
      {writable && (<>
      <h2>Issue codes</h2>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <label>Wave <input value={wave} onChange={(e) => setWave(e.target.value)} style={{ padding: 6, borderRadius: 6, border: '1px solid #d1d5db' }} /></label>
        <label>Count <input type="number" value={count} onChange={(e) => setCount(parseInt(e.target.value) || 0)} style={{ width: 70, padding: 6, borderRadius: 6, border: '1px solid #d1d5db' }} /></label>
        <button style={btn} disabled={busy} onClick={doGenerate}>Generate pool codes</button>
        <span style={{ color: '#d1d5db' }}>|</span>
        <button style={{ ...btn, background: '#0F5132' }} disabled={busy} onClick={() => { setJustCreated(null); setShowNamed(true); }}>
          + One-off code
        </button>
        <button style={{ ...btn, background: '#374151' }} disabled={busy} onClick={doExport}>Export CSV</button>
      </div>

      {/* Just-minted one-off — shown until the next action so it can be copied out. */}
      {justCreated && (
        <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 8, padding: 12, marginBottom: 16 }}>
          <strong style={{ fontSize: 18, letterSpacing: 0.5 }}>{justCreated.code}</strong>
          <span style={{ color: '#065F46', marginLeft: 10 }}>{justCreated.label} of full access · send this to them directly</span>
          <button
            style={{ ...btn, padding: '4px 10px', fontSize: 12, marginLeft: 12, background: '#374151' }}
            onClick={() => { navigator.clipboard?.writeText(justCreated.code); }}
          >Copy</button>
        </div>
      )}

      {/* One-off code form. Deliberately just two fields: who it's for, and how long. */}
      {showNamed && (
        <div style={{ ...card, marginBottom: 16, maxWidth: 560 }}>
          <h3 style={{ marginTop: 0 }}>One-off code</h3>
          <p style={{ color: '#6b7280', fontSize: 13, marginTop: 0 }}>
            For an evaluator or influencer. Doesn't use a Summer50 spot, never expires until
            redeemed, and the recipient sees no campaign branding.
          </p>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, color: '#6b7280' }}>
              First name
              <input
                autoFocus
                value={namedFirst}
                onChange={(e) => setNamedFirst(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !busy) doCreateNamed(); }}
                placeholder="Paul"
                style={{ padding: 8, borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, marginTop: 4 }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, color: '#6b7280' }}>
              Length
              <select
                value={namedDuration}
                onChange={(e) => setNamedDuration(e.target.value)}
                style={{ padding: 8, borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, marginTop: 4 }}
              >
                {durations.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, color: '#6b7280', flex: 1, minWidth: 160 }}>
              Note (optional)
              <input
                value={namedNote}
                onChange={(e) => setNamedNote(e.target.value)}
                placeholder="Podcast host, met at the expo"
                style={{ padding: 8, borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, marginTop: 4 }}
              />
            </label>
            <button style={btn} disabled={busy || !namedFirst.trim() || durations.length === 0} onClick={doCreateNamed}>
              {busy ? '…' : 'Generate'}
            </button>
            <button style={{ ...btn, background: '#6b7280' }} disabled={busy} onClick={() => setShowNamed(false)}>Cancel</button>
          </div>
          {durations.length === 0 && (
            <p style={{ color: '#B91C1C', fontSize: 12, marginBottom: 0 }}>
              Couldn't load duration options — reload the page before issuing.
            </p>
          )}
        </div>
      )}
      </>)}

      {/* Codes table */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Codes</h2>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ padding: 6, borderRadius: 6 }}>
          <option value="">all statuses</option>
          {['pooled', 'allocated', 'issued', 'redeemed', 'expired', 'revoked'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ padding: 6, borderRadius: 6 }}>
          <option value="">all types</option>
          <option value="campaign">campaign</option>
          <option value="strategic">one-off</option>
        </select>
        <button style={{ ...btn, background: '#374151' }} onClick={refresh}>Refresh</button>
      </div>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead><tr><th style={th}>Code</th><th style={th}>Type</th><th style={th}>Length</th><th style={th}>Wave</th><th style={th}>Status</th>{writable && <th style={th}>For</th>}{writable && <th style={th}>Note</th>}<th style={th}>Expires</th>{writable && <th style={th}>Actions</th>}</tr></thead>
        <tbody>
          {codes.map((c) => {
            const oneOff = c.code_type === 'strategic';
            return (
            <tr key={c.id}>
              <td style={td}><code>{c.code}</code></td>
              <td style={td}>
                <span style={{
                  fontSize: 11, padding: '2px 7px', borderRadius: 10, fontWeight: 600,
                  background: oneOff ? '#ECFDF5' : '#F3F4F6', color: oneOff ? '#065F46' : '#374151',
                }}>{oneOff ? 'one-off' : 'campaign'}</span>
              </td>
              <td style={td}>{c.duration_label}</td>
              <td style={td}>{c.wave || '—'}</td>
              <td style={td}>{c.status}</td>
              {writable && <td style={td}>{c.allocated_to_handle || '—'}</td>}
              {/* Why the code was issued — typed in the one-off form's Note field (issued_to_note).
                  Shown in the row, not only as a hover tooltip (owner, 2026-09-30). */}
              {writable && <td style={{ ...td, maxWidth: 280, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{c.note || '—'}</td>}
              <td style={td}>{c.expires_at ? new Date(c.expires_at).toLocaleString() : '—'}</td>
              {writable && <td style={td}>
                {c.status === 'pooled' && <button style={{ ...btn, padding: '4px 8px', fontSize: 12 }} disabled={busy}
                  onClick={() => { const h = prompt('Allocate to handle/note:'); if (h) act(() => Svc.allocate(c.id, h)); }}>Allocate</button>}
                {/* +72h only makes sense for pool codes on the allocation clock; one-off
                    codes are issued with no expiry, so there is nothing to extend. */}
                {c.status === 'allocated' && <button style={{ ...btn, padding: '4px 8px', fontSize: 12, background: '#374151' }} disabled={busy}
                  onClick={() => act(() => Svc.extendExpiry(c.id, 72))}>+72h</button>}
                {c.status !== 'revoked' && c.status !== 'redeemed' && <button style={{ ...btn, padding: '4px 8px', fontSize: 12, background: '#B91C1C', marginLeft: 6 }} disabled={busy}
                  onClick={() => { if (confirm('Revoke code?')) act(() => Svc.revokeCode(c.id)); }}>Revoke</button>}
              </td>}
            </tr>
            );
          })}
        </tbody>
      </table>
      {codes.length === 0 && <p style={{ color: '#6b7280' }}>{writable ? 'No codes yet — generate a wave or issue a one-off above.' : 'No codes yet.'}</p>}
    </div>
  );
}

export default AdminGrowth;
